import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Circle, Copy, ExternalLink, RefreshCw, Trash2 } from "lucide-react";
import { adminApi } from "@/lib/api";
import { AnnounceCard } from "./AdminReviews";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-SG", { dateStyle: "medium", timeStyle: "short" }) : "—");
const detail = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : "Something went wrong."; };

const ModeSwitch = ({ mode, onChange, busy }) => {
  const live = mode === "live";
  const [confirm, setConfirm] = useState(false);
  const flip = () => { if (!confirm) return setConfirm(true); setConfirm(false); onChange(live ? "maintenance" : "live"); };
  return (
    <div className={`launch-mode${live ? " is-live" : ""}`} data-testid="launch-mode-card">
      <div className="launch-mode-copy">
        <span className="eyebrow">Website status</span>
        <h2 data-testid="launch-mode-label">{live ? "Live for the public" : "Coming Soon mode"}</h2>
        <p>{live ? "Visitors see the full website, can buy the eBook and book a 5Ps Conversation." : "Visitors see a branded Coming Soon page with an email sign-up. The full site stays reachable through your private preview link."}</p>
      </div>
      <div className="launch-mode-actions">
        <button type="button" className={`launch-toggle${live ? " on" : ""}`} onClick={flip} disabled={busy} aria-pressed={live} aria-label="Toggle website mode" data-testid="launch-mode-toggle"><i /></button>
        <span className="launch-toggle-label">{live ? "LIVE" : "MAINTENANCE"}</span>
        {confirm && (
          <div className="launch-confirm" data-testid="launch-confirm">
            <p>{live ? "Hide the website behind the Coming Soon page?" : "Make the full website public now?"}</p>
            <button className="btn btn-gold" type="button" onClick={flip} data-testid="launch-confirm-yes">{live ? "Yes, switch to Coming Soon" : "Yes, go live"}</button>
            <button className="admin-link admin-link-muted" type="button" onClick={() => setConfirm(false)} data-testid="launch-confirm-no">Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
};

export const LaunchView = ({ onUnauthorized, onGoTab, onModeChange }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const d = await adminApi.launch(); setData(d); onModeChange?.(d.mode); }
    catch (err) { if (err?.response?.status === 401) onUnauthorized(); else toast.error(detail(err)); }
  }, [onUnauthorized, onModeChange]);
  useEffect(() => { load(); }, [load]);

  const setMode = async (mode) => {
    setBusy(true);
    try { await adminApi.setSiteMode(mode); toast.success(mode === "live" ? "Your website is now LIVE." : "Website is now in Coming Soon mode."); await load(); }
    catch (err) { toast.error(detail(err)); }
    finally { setBusy(false); }
  };
  const rotate = async () => {
    try { await adminApi.rotatePreview(); toast.success("New preview link generated. The old one no longer works."); await load(); }
    catch (err) { toast.error(detail(err)); }
  };
  if (!data) return <div className="admin-empty" data-testid="launch-loading">Loading launch status...</div>;

  const previewUrl = `${window.location.origin}/?preview=${data.previewToken}`;
  const done = data.checklist.filter((c) => c.done).length;
  const copy = async () => { try { await navigator.clipboard.writeText(previewUrl); toast.success("Preview link copied"); } catch { toast.error("Could not copy. Select the link and copy manually."); } };

  return (
    <div data-testid="admin-launch">
      <div className="admin-title"><span className="eyebrow">Go live when you are ready</span><h1>Launch control</h1></div>
      <ModeSwitch mode={data.mode} onChange={setMode} busy={busy} />
      <div className="launch-grid">
        <div className="launch-card" data-testid="launch-preview-card">
          <h3>Private preview link</h3>
          <p className="cms-hint">Open the full website while it is hidden from the public. Share it only with people you trust.</p>
          <div className="launch-link"><code data-testid="launch-preview-url">{previewUrl}</code></div>
          <div className="launch-link-actions">
            <button className="btn btn-gold" type="button" onClick={copy} data-testid="launch-copy-preview"><Copy size={14} /> Copy link</button>
            <a className="admin-ghost admin-ghost-dark" href={previewUrl} target="_blank" rel="noopener noreferrer" data-testid="launch-open-preview"><ExternalLink size={14} /> Open preview</a>
            <button className="admin-link admin-link-muted" type="button" onClick={rotate} data-testid="launch-rotate-preview"><RefreshCw size={13} /> Generate new link</button>
          </div>
        </div>
        <div className="launch-card" data-testid="launch-checklist-card">
          <h3>Launch checklist <span className="cms-count" data-testid="launch-checklist-count">{done}/{data.checklist.length}</span></h3>
          <div className="launch-progress"><i style={{ width: `${(done / data.checklist.length) * 100}%` }} /></div>
          <ul className="launch-list">
            {data.checklist.map((c) => (
              <li key={c.id} className={c.done ? "done" : ""} data-testid={`launch-check-${c.id}`}>
                <span className="launch-check">{c.done ? <Check size={14} /> : <Circle size={12} />}</span>
                <div>
                  <strong>{c.label}</strong>
                  {!c.done && <small>{c.hint}</small>}
                  {!c.done && c.tab && <button type="button" className="admin-link" onClick={() => onGoTab(c.tab)} data-testid={`launch-fix-${c.id}`}>Fix this →</button>}
                </div>
              </li>
            ))}
          </ul>
          <p className="cms-hint" style={{ marginTop: 12 }}>Payments: <strong>{data.stripeMode === "live" ? "Stripe live mode" : "Stripe test mode (card 4242 4242 4242 4242)"}</strong></p>
        </div>
      </div>
      <AnnounceCard siteMode={data.mode} />
    </div>
  );
};

const TYPE_LABEL = { notify: "Launch list", sample: "Free lesson" };

export const LeadsView = ({ onUnauthorized }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await adminApi.leads()); }
    catch (err) { if (err?.response?.status === 401) onUnauthorized(); else toast.error(detail(err)); }
    finally { setLoading(false); }
  }, [onUnauthorized]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    try { await adminApi.deleteLead(id); setRows((r) => r.filter((x) => x.id !== id)); toast.success("Removed"); }
    catch (err) { toast.error(detail(err)); }
  };
  const exportCsv = async () => {
    try {
      const blob = await adminApi.exportLeadsCsv();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `transitionworks-leads-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("Spreadsheet downloaded");
    } catch (err) { toast.error(detail(err)); }
  };
  const visible = rows.filter((r) => filter === "all" || r.type === filter);

  return (
    <div data-testid="admin-leads">
      <div className="admin-title"><span className="eyebrow">Email sign-ups</span><h1>Leads</h1></div>
      <div className="admin-stats">
        <div className="admin-stat" data-testid="leads-stat-total"><span>Total emails</span><strong>{rows.length}</strong></div>
        <div className="admin-stat" data-testid="leads-stat-notify"><span>Launch list</span><strong>{rows.filter((r) => r.type === "notify").length}</strong></div>
        <div className="admin-stat" data-testid="leads-stat-sample"><span>Free lesson requests</span><strong>{rows.filter((r) => r.type === "sample").length}</strong></div>
        <div className="admin-stat" data-testid="leads-stat-followup"><span>Follow-ups sent (day 2)</span><strong>{rows.filter((r) => r.type === "sample" && r.followupSent && !r.followupSkipped).length}</strong></div>
        <div className="admin-stat" data-testid="leads-stat-converted"><span>Bought after free lesson</span><strong>{rows.filter((r) => r.type === "sample" && r.purchased).length}</strong></div>
      </div>
      <div className="admin-toolbar">
        <div className="admin-filters">
          {["all", "notify", "sample"].map((t) => <button key={t} className={`filter-btn${filter === t ? " active" : ""}`} onClick={() => setFilter(t)} data-testid={`leads-filter-${t}`}>{t === "all" ? "All" : TYPE_LABEL[t]}</button>)}
        </div>
        <div className="admin-header-actions">
          <button className="admin-ghost admin-ghost-dark" onClick={exportCsv} data-testid="leads-export-btn">Export spreadsheet</button>
          <button className="admin-ghost admin-ghost-dark" onClick={load} data-testid="leads-refresh-btn">Refresh</button>
        </div>
      </div>
      <div className="admin-table-wrap">
        {loading && !rows.length ? <div className="admin-empty">Loading leads...</div>
          : !visible.length ? <div className="admin-empty" data-testid="leads-empty">No sign-ups yet. They will appear here as visitors join the launch list or request the free lesson.</div> : (
          <table className="admin-table" data-testid="leads-table">
            <thead><tr><th>Date</th><th>Email</th><th>Type</th><th>Sent</th><th /></tr></thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} data-testid={`lead-row-${r.id}`}>
                  <td className="admin-muted">{fmt(r.createdAt)}</td>
                  <td><a href={`mailto:${r.email}`}>{r.email}</a>{r.name && <><br /><span className="admin-muted">{r.name}</span></>}</td>
                  <td><span className="admin-pill">{TYPE_LABEL[r.type] || r.type}</span>{r.lessonTitle && <><br /><span className="admin-muted">{r.lessonTitle}</span></>}</td>
                  <td>
                    {r.emailSent ? <span className="admin-pill">Email sent</span> : <span className="admin-pill" style={{ background: "rgba(180,70,47,.12)", color: "#b4462f" }}>Not sent</span>}
                    {r.type === "sample" && r.followupSent && !r.followupSkipped && <><br /><span className="admin-pill" data-testid={`lead-followup-${r.id}`}>Follow-up sent</span></>}
                    {r.purchased && <><br /><span className="admin-pill" style={{ background: "rgba(13,42,36,.1)", color: "#0D2A24" }} data-testid={`lead-bought-${r.id}`}>Bought the eBook</span></>}
                  </td>
                  <td><button className="admin-link admin-link-muted" onClick={() => remove(r.id)} aria-label="Remove" data-testid={`lead-delete-${r.id}`}><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
